import { Controller } from "@hotwired/stimulus";

/**
 * PWA Orientation Warning Controller
 *
 * Displays an overlay on iOS devices when in landscape orientation,
 * prompting the user to rotate back to portrait mode. This is needed
 * because iOS does not respect the manifest's orientation lock.
 *
 * @extends Controller
 */
export default class extends Controller {
  static targets = ["overlay"];

  /**
   * Initialize the controller and set up orientation detection
   */
  connect() {
    // Only activate for iOS devices
    if (!this.isIOS()) {
      return;
    }

    this.checkOrientation = this.checkOrientation.bind(this);
    this.setupEventListeners();
    this.checkOrientation();
  }

  /**
   * Clean up event listeners on disconnect
   */
  disconnect() {
    window.removeEventListener("orientationchange", this.checkOrientation);
    window.removeEventListener("resize", this.checkOrientation);
  }

  /**
   * Detect if the device is running iOS
   * @returns {boolean} True if iOS device
   * @private
   */
  isIOS() {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent) ||
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  /**
   * Set up event listeners for orientation changes
   * @private
   */
  setupEventListeners() {
    // Listen for orientation change event
    window.addEventListener("orientationchange", this.checkOrientation);

    // Also listen for resize as a fallback (some iOS versions)
    window.addEventListener("resize", this.checkOrientation);
  }

  /**
   * Check current orientation and show/hide overlay accordingly
   * @private
   */
  checkOrientation() {
    const isLandscape = this.isLandscapeOrientation();

    if (isLandscape) {
      this.showOverlay();
    } else {
      this.hideOverlay();
    }
  }

  /**
   * Determine if device is in landscape orientation
   * @returns {boolean} True if landscape
   * @private
   */
  isLandscapeOrientation() {
    // Use screen.orientation API if available
    if (screen.orientation && screen.orientation.type) {
      return screen.orientation.type.includes("landscape");
    }

    // Fallback to window.orientation (deprecated but widely supported on iOS)
    if (typeof window.orientation !== "undefined") {
      return Math.abs(window.orientation) === 90;
    }

    // Final fallback: compare dimensions
    return window.innerWidth > window.innerHeight;
  }

  /**
   * Show the orientation warning overlay
   * @private
   */
  showOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden");
      this.overlayTarget.classList.add("flex");
    }
  }

  /**
   * Hide the orientation warning overlay
   * @private
   */
  hideOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden");
      this.overlayTarget.classList.remove("flex");
    }
  }
}
